import { FC, useContext, useMemo, useState } from "react";
import { StatusListItemTooltip } from "../lists/status/StatusListStats";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Paper } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppSettings } from "@/AppSettings";
import { Link, useParams } from "react-router-dom/dist";
import SourceDataContext from "@/contexts/data/SourceDataContext";
import { SourceDataEventStatus, SourceDataFormStatus } from "@/api/dtos";
import TrialContext from "@/contexts/data/TrialContext";
import SourceDataFormSettingsContext from "@/contexts/form/SourceDataFormSettingsContext";

interface IOpenClinicaEventLinkProps {
    studyNumber?: string;
    studyEventOid?: string;
    studyEventRepeat?: number;
    crfOid?: number;
}

interface IOpenClinicaEventLinkDialogProps extends DialogProps {
    url?: string;
    handleClose: () => void;
}

const OpenClinicaEventLinkDialog: FC<IOpenClinicaEventLinkDialogProps> = ({
    url,
    handleClose,
    ...props
}) => {
    const { data: trial } = useContext(TrialContext)

    const warningContent = useMemo(() => {
        return trial ?
            `Please ensure that you have selected the appropriate study (${trial.name}) in OpenClinica before continuing. Failure to do so may result in unexpected behaviour.` :
            "Please ensure that you have selected the appropriate study in OpenClinica before continuing. Failure to do so may result in unexpected behaviour.";
    }, [trial]);

    if(!url) {
        return null;
    }

    return (
        <Dialog
            {...props}
        >
            <DialogTitle>Confirm</DialogTitle>
            <DialogContent>
                <Alert 
                    severity="warning"
                >
                    {warningContent}
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    component={Link}
                    target="_blank"
                    to={url}
                    onClick={handleClose}
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const OpenClinicaEventLink: FC<IOpenClinicaEventLinkProps> = (props) => {
    const params = useParams();
    const [dialogOpen, setDialogOpen] = useState(false);

    const studyNumberToUse = useMemo(() => props.studyNumber ?? params.studyNumber, [props.studyNumber, params.studyNumber]);
    const studyEventOidToUse = useMemo(() => props.studyEventOid ?? params.studyEventOid, [props.studyEventOid, params.studyEventOid]);

    const studyEventRepeatToUse = useMemo(() => {
        if(props.studyEventRepeat) {
            return props.studyEventRepeat;
        }
        if(params.studyEventRepeat && !Number.isNaN(+params.studyEventRepeat)) {
            return +params.studyEventRepeat;
        }
        return undefined;
    }, [props.studyEventRepeat, params.studyEventRepeat]);

    const { data: sourceData } = useContext(SourceDataContext);

    const event = useMemo(() => (
        (studyEventOidToUse && studyEventRepeatToUse) 
            ? sourceData?.events?.[studyEventOidToUse]?.[studyEventRepeatToUse] 
            : undefined
    ), [sourceData, studyEventOidToUse, studyEventRepeatToUse]);

    const disabled = useMemo(() => {
        return !event?.status || event.status === SourceDataEventStatus.None;
    }, [event]);

    const ocUrlAction = useMemo(() => {
        if (event?.status === SourceDataEventStatus.None) {
            return undefined;
        }
        
        return `EnterDataForStudyEvent?eventId=${event?.id}`
    }, [event]);

    const url = useMemo(() => `${AppSettings.openClinicaUrl}OpenClinica/${ocUrlAction}`, [AppSettings.openClinicaUrl, ocUrlAction]);
        
    
        const { 
            settings: { 
                hideOpenClinicaLinkWarning
            } 
        } = useContext(SourceDataFormSettingsContext);

    if(!studyNumberToUse || !studyEventOidToUse || !studyEventRepeatToUse || !ocUrlAction) {
        return null;
    }

    return (
        <>
            <OpenClinicaEventLinkDialog 
                open={dialogOpen}
                url={url}
                handleClose={() => setDialogOpen(false)}
            />
            <StatusListItemTooltip 
                title={
                    <>
                        <strong>Open Event in OpenClinica</strong>
                    </>
                }
            >
                <Paper>
                    {
                        hideOpenClinicaLinkWarning ?
                            (
                                <Button
                                    component="a"
                                    href={url}
                                    target="_blank"
                                    color="inherit"
                                    disabled={disabled}
                                    sx={{
                                        minWidth: 0
                                    }}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.externalLink} size="lg"  />
                                </Button>
                            ) :
                            (
                                <Button
                                    color="inherit"
                                    disabled={disabled}
                                    onClick={() => setDialogOpen(true)}
                                    sx={{
                                        minWidth: 0
                                    }}
                                >
                                    <FontAwesomeIcon icon={AppSettings.icons.externalLink} size="lg"  />
                                </Button>
                            )
                    }
                </Paper>
            </StatusListItemTooltip>
        </>
    )
}

export default OpenClinicaEventLink;